<template>
  <v-dialog v-model="internalValue" max-width="700" persistent>
    <v-card>
      <v-card-text class="primary white--text d-flex align-center py-2 px-4">
        <v-icon class="white--text mr-2">mdi-robot</v-icon>
        <span class="font-size-md white--text">AI Commentary Generation</span>
        <v-spacer />
        <v-btn icon @click="close" class="ml-2 white--text"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-text>
      <v-card-text class="pa-4">
        <!-- Status Summary -->
        <div class="d-flex align-center mb-3">
          <v-icon :color="statusColor" class="mr-2">{{ statusIcon }}</v-icon>
          <span class="font-weight-medium">{{ statusText }}</span>
        </div>

        <!-- Progress Bar -->
        <v-progress-linear
          :value="progressPercent"
          :color="progressColor"
          height="20"
          rounded
          class="mb-2"
        >
          <template v-slot:default>
            <span class="white--text caption font-weight-bold">{{ progressPercent }}%</span>
          </template>
        </v-progress-linear>

        <!-- Counts -->
        <div class="d-flex justify-space-between caption grey--text text--darken-1 mb-4">
          <span>{{ completed }} / {{ total }} completed</span>
          <span v-if="failed > 0" class="error--text">{{ failed }} failed</span>
        </div>

        <!-- Detailed Breakdown (while processing) -->
        <v-simple-table dense v-if="total > 0 && !isFinished">
          <tbody>
            <tr>
              <td class="caption">Queued</td>
              <td class="caption text-right">{{ queued }}</td>
            </tr>
            <tr>
              <td class="caption">Processing</td>
              <td class="caption text-right">{{ processing }}</td>
            </tr>
            <tr>
              <td class="caption">Completed</td>
              <td class="caption text-right font-weight-bold success--text">{{ completed }}</td>
            </tr>
            <tr v-if="failed > 0">
              <td class="caption">Failed</td>
              <td class="caption text-right font-weight-bold error--text">{{ failed }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Results (when done) -->
        <div v-if="isFinished && groupedResults.length > 0" class="mt-2">
          <div class="caption font-weight-bold mb-2">Generated Commentary</div>
          <div style="max-height: 400px; overflow-y: auto;">
            <v-expansion-panels accordion flat>
              <v-expansion-panel v-for="(group, idx) in groupedResults" :key="idx">
                <v-expansion-panel-header class="pa-2" style="min-height: 40px;">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" :color="group.allCompleted ? 'success' : 'error'">{{ group.allCompleted ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                    <span class="caption font-weight-medium">{{ group.gl_name }}</span>
                    <v-spacer />
                    <v-chip x-small color="blue" dark class="mr-1" v-if="group.proforma">Proforma</v-chip>
                    <v-chip x-small color="orange" dark v-if="group.forecast">Forecast</v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-0">
                  <div v-if="group.proforma" class="px-3 pb-2">
                    <div class="caption font-weight-bold blue--text mb-1">Proforma</div>
                    <div class="caption grey--text text--darken-2" style="white-space: pre-wrap; line-height: 1.5;">{{ group.proforma }}</div>
                  </div>
                  <v-divider v-if="group.proforma && group.forecast" />
                  <div v-if="group.forecast" class="px-3 py-2">
                    <div class="caption font-weight-bold orange--text text--darken-2 mb-1">Forecast</div>
                    <div class="caption grey--text text--darken-2" style="white-space: pre-wrap; line-height: 1.5;">{{ group.forecast }}</div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <!-- Batch ID for reference -->
        <div class="mt-3 caption grey--text">
          Batch: {{ batchId || '—' }}
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn small text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AIBatchProgressDialog',
  props: {
    value: { type: Boolean, default: false },
    batchId: { type: String, default: null },
  },
  data() {
    return {
      total: 0,
      queued: 0,
      dispatched: 0,
      processing: 0,
      completed: 0,
      failed: 0,
      skipped: 0,
      pollTimer: null,
      isFinished: false,
      jobResults: [],
    };
  },
  computed: {
    internalValue: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
    progressPercent() {
      if (!this.total) return 0;
      return Math.round(((this.completed + this.failed + this.skipped) / this.total) * 100);
    },
    progressColor() {
      if (this.isFinished && this.failed > 0) return 'warning';
      if (this.isFinished) return 'success';
      return 'info';
    },
    statusColor() {
      if (this.isFinished && this.failed > 0) return 'warning';
      if (this.isFinished) return 'success';
      return 'info';
    },
    statusIcon() {
      if (this.isFinished && this.failed > 0) return 'mdi-alert-circle';
      if (this.isFinished) return 'mdi-check-circle';
      return 'mdi-progress-clock';
    },
    statusText() {
      if (!this.batchId) return 'No batch in progress';
      if (this.isFinished && this.failed > 0) return `Done — ${this.completed} succeeded, ${this.failed} failed`;
      if (this.isFinished) return `Completed — ${this.completed} commentary generated`;
      return 'Generating AI commentary...';
    },
    groupedResults() {
      if (!this.jobResults.length) return [];
      const map = {};
      for (const r of this.jobResults) {
        const key = r.gl_code;
        if (!map[key]) {
          map[key] = { gl_code: r.gl_code, gl_name: r.gl_name, proforma: null, forecast: null, allCompleted: true };
        }
        if (r.type_of_data === 'proforma') {
          map[key].proforma = r.commentary || '';
        } else if (r.type_of_data === 'forecast') {
          map[key].forecast = r.commentary || '';
        }
        if (r.status !== 'completed') map[key].allCompleted = false;
      }
      return Object.values(map);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open && this.batchId) {
          this.startPolling();
        } else if (!open) {
          this.stopPolling();
        }
      },
    },
    batchId(newId) {
      if (newId && this.value) {
        this.resetState();
        this.startPolling();
      }
    },
  },
  methods: {
    close() {
      this.stopPolling();
      this.internalValue = false;
      this.$emit('closed');
    },
    resetState() {
      this.total = 0;
      this.queued = 0;
      this.dispatched = 0;
      this.processing = 0;
      this.completed = 0;
      this.failed = 0;
      this.skipped = 0;
      this.isFinished = false;
      this.jobResults = [];
    },
    startPolling() {
      this.stopPolling();
      if (!this.batchId) return;
      this.fetchStatus();
      this.pollTimer = setInterval(() => {
        this.fetchStatus();
      }, 5000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async fetchStatus() {
      if (!this.batchId) return;
      try {
        const url = `/v2/ai/batch-status/${this.batchId}`;
        const response = await this.axios.post(url);
        const data = response?.data?.data || response?.data || {};
        
        this.total = data.total || this.total;
        this.queued = (data.queued || 0) + (data.dispatched || 0);
        this.processing = data.processing || 0;
        this.completed = data.completed || 0;
        this.failed = data.failed || 0;
        this.skipped = data.skipped || 0;

        // Capture results when batch is done
        if (data.job_results && data.job_results.length > 0) {
          this.jobResults = data.job_results;
        }

        const done = this.completed + this.failed + this.skipped;
        if (this.total > 0 && done >= this.total) {
          this.isFinished = true;
          this.stopPolling();
        }
      } catch (e) {
        console.error('[AIBatchProgress] Poll error:', e?.response?.status, e?.response?.data || e.message);
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>
